import { Fragment, useState, type ReactNode } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  deletePhoto,
  deleteUser,
  getUserDetail,
  getUserPhotos,
  mediaUrl,
  patchUser,
  patchUserStatus,
  resetUser,
  setPhotoCover,
  type AdminPhoto,
  type UserStatus,
} from '../api';
import { badgeClass, fmtDate } from '../format';
import { useApi } from '../useApi';
import { Icon } from '../components/Icon';

const ACTION_LABEL: Record<UserStatus, string> = { active: 'Activate', suspended: 'Suspend', banned: 'Ban' };
const STATUSES: UserStatus[] = ['active', 'suspended', 'banned'];
const PLAN_IDS = ['free', 'spark', 'pulse', 'infinity'] as const;
type PlanId = (typeof PLAN_IDS)[number];

const DASH = <span className="muted">—</span>;
const yesNo = (v: boolean) => (v ? 'Yes' : DASH);

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children ?? DASH}</dd>
    </>
  );
}
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="detail-section">
      <h3>{title}</h3>
      <dl>{children}</dl>
    </div>
  );
}

function initial(name: string | null | undefined): string {
  return (name ?? '?').trim().charAt(0).toUpperCase() || '?';
}

/** A user's photos as thumbnails, each deletable. */
function PhotosPanel({ userId }: { userId: string }) {
  const { data, error, loading, reload } = useApi(() => getUserPhotos(userId), [userId]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const items = data?.items ?? [];

  const remove = async (p: AdminPhoto) => {
    if (!window.confirm('Delete this photo? It is removed from storage permanently.')) return;
    setBusyId(p.id);
    setActionError(null);
    try {
      await deletePhoto(p.id);
      reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Delete failed.');
    } finally {
      setBusyId(null);
    }
  };

  const makeCover = async (p: AdminPhoto) => {
    setBusyId(p.id);
    setActionError(null);
    try {
      await setPhotoCover(p.id);
      reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not set main photo.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="detail-section" style={{ gridColumn: '1 / -1' }}>
      <h3>Photos</h3>
      {error ? (
        <span className="error-line">{error}</span>
      ) : loading && !data ? (
        <span className="muted">Loading photos…</span>
      ) : items.length === 0 ? (
        <span className="muted">No photos.</span>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          {items.map((p) => (
            <figure key={p.id} style={{ margin: 0, textAlign: 'center' }}>
              <div style={{ position: 'relative', width: 96 }}>
                <a href={mediaUrl(p.url)} target="_blank" rel="noreferrer">
                  <img
                    src={mediaUrl(p.url)}
                    alt={p.isCover ? 'main photo' : 'photo'}
                    style={{
                      width: 96,
                      height: 96,
                      objectFit: 'cover',
                      borderRadius: 12,
                      display: 'block',
                      border: p.isCover ? '2px solid var(--accent)' : '1px solid var(--border)',
                    }}
                  />
                </a>
                {p.isCover && (
                  <span
                    style={{
                      position: 'absolute',
                      top: 5,
                      left: 5,
                      padding: '2px 7px',
                      borderRadius: 999,
                      fontSize: '0.66rem',
                      fontWeight: 700,
                      background: 'var(--accent)',
                      color: 'var(--accent-contrast)',
                      boxShadow: '0 1px 6px rgba(0,0,0,0.4)',
                    }}
                  >
                    ★ Main
                  </span>
                )}
              </div>
              <figcaption style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
                <span className={badgeClass(p.moderationStatus)}>{p.moderationStatus}</span>
                {!p.isCover && (
                  <button
                    className="btn"
                    disabled={busyId === p.id || p.moderationStatus === 'rejected'}
                    title={p.moderationStatus === 'rejected' ? 'Rejected photos cannot be the main photo' : 'Set as main photo'}
                    onClick={() => makeCover(p)}
                  >
                    Set as main
                  </button>
                )}
                <button className="btn btn-danger" disabled={busyId === p.id} onClick={() => remove(p)}>
                  Delete
                </button>
              </figcaption>
            </figure>
          ))}
        </div>
      )}
      {actionError && <p className="error-line">{actionError}</p>}
    </div>
  );
}

export function UserDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { data: d, error, loading, reload } = useApi(() => getUserDetail(id), [id]);

  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [grantPlan, setGrantPlan] = useState<PlanId>('spark');
  const [grantDays, setGrantDays] = useState('30');

  const run = async (fn: () => Promise<unknown>, after?: () => void) => {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
      after ? after() : reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Action failed.');
    } finally {
      setBusy(false);
    }
  };

  if (loading && !d) return <div className="admin-wrap"><p className="muted">Loading user…</p></div>;
  if (error || !d) return <div className="admin-wrap"><p className="error-line">{error ?? 'Not found.'}</p><Link className="btn" to="/users">← Users</Link></div>;

  const { user, telegram: tg, profile: p, preference: pref, location: loc, counts: c } = d;
  const name = p?.displayName ?? tg?.firstName ?? (tg?.username ? '@' + tg.username : 'User');
  const effectivePlan = d.subscriptions[0]?.planId ?? 'free';

  return (
    <div className="admin-wrap">
      <Link className="link-btn" to="/users" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 14, textDecoration: 'none' }}>
        <Icon name="chevronLeft" size={15} /> Users
      </Link>

      {/* header */}
      <div className="card" style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
        {tg?.photoUrl ? (
          <img className="avatar lg" src={tg.photoUrl} alt={name} referrerPolicy="no-referrer" />
        ) : (
          <span className="avatar lg">{initial(name)}</span>
        )}
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h1 style={{ margin: 0, fontSize: '1.5rem' }}>
              {name}
              {p?.age != null && <span className="muted" style={{ fontWeight: 400 }}>, {p.age}</span>}
            </h1>
            <span className={badgeClass(user.status)}>{user.status}</span>
            {user.isPremium && <span className={`plan-badge ${effectivePlan}`}>{effectivePlan}</span>}
            {p?.isVerified && <span className="badge badge-ok">verified</span>}
          </div>
          <p className="muted" style={{ margin: '6px 0 0', fontSize: '0.86rem' }}>
            {tg?.username ? `@${tg.username} · ` : ''}
            {[p?.city, p?.country].filter(Boolean).join(', ') || 'No location'} · joined {fmtDate(user.createdAt)}
          </p>
        </div>
      </div>

      {actionError && <p className="error-line" style={{ marginTop: 12 }}>{actionError}</p>}

      {/* action bar */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-head"><span className="card-title">Actions</span></div>
        <div className="row-actions">
          {STATUSES.filter((s) => s !== user.status).map((s) => (
            <button key={s} className={s === 'banned' ? 'btn btn-danger' : 'btn'} disabled={busy} onClick={() => run(() => patchUserStatus(id, s))}>
              {ACTION_LABEL[s]}
            </button>
          ))}
          <span className="row-actions" style={{ gap: 4 }}>
            <select className="input cell" value={grantPlan} disabled={busy} onChange={(e) => setGrantPlan(e.target.value as PlanId)}>
              {PLAN_IDS.map((pl) => <option key={pl} value={pl}>{pl}</option>)}
            </select>
            <input
              className="input cell num"
              style={{ width: 56 }}
              value={grantDays}
              disabled={busy || grantPlan === 'free'}
              onChange={(e) => setGrantDays(e.target.value)}
              title={grantPlan === 'free' ? 'Days ignored when downgrading to Free' : 'Days'}
            />
            <button
              className="btn"
              disabled={busy}
              onClick={() =>
                run(() =>
                  grantPlan === 'free'
                    ? patchUser(id, { planId: 'free' })
                    : patchUser(id, { premiumDays: Math.max(1, Math.floor(Number(grantDays) || 30)), planId: grantPlan }),
                )
              }
            >
              {grantPlan === 'free' ? 'Set Free' : user.isPremium ? 'Extend / change' : 'Grant'}
            </button>
          </span>
          {user.isPremium && (
            <button className="btn" disabled={busy} onClick={() => run(() => patchUser(id, { revokePremium: true }))}>
              Revoke premium
            </button>
          )}
          <button className="btn" disabled={busy} title="Clear swipes & matches so this user can be re-tested" onClick={() => { if (window.confirm("Clear this user's swipes & matches?")) run(() => resetUser(id)); }}>
            Reset
          </button>
          <button className="btn btn-danger" disabled={busy} onClick={() => { if (window.confirm(`Delete ${name} and ALL their data? This cannot be undone.`)) run(() => deleteUser(id), () => navigate('/users')); }}>
            Delete
          </button>
        </div>
      </div>

      {/* details */}
      <div className="detail-grid" style={{ marginTop: 16 }}>
        <Section title="Identity">
          <Row label="User ID"><span className="mono">{user.id}</span></Row>
          <Row label="Status"><span className={badgeClass(user.status)}>{user.status}</span></Row>
          <Row label="Locale">{user.locale ?? DASH}</Row>
          <Row label="18+ verified">{yesNo(user.ageVerified18)}</Row>
          <Row label="Created">{fmtDate(user.createdAt)}</Row>
          <Row label="Last active">{fmtDate(user.lastActiveAt)}</Row>
          <Row label="TG user ID">{tg ? <span className="mono">{tg.tgUserId}</span> : DASH}</Row>
          <Row label="TG username">{tg?.username ? `@${tg.username}` : DASH}</Row>
          <Row label="TG first name">{tg?.firstName ?? DASH}</Row>
          <Row label="TG language">{tg?.languageCode ?? DASH}</Row>
          <Row label="Allows bot PM">{tg ? yesNo(tg.allowsWriteToPm) : DASH}</Row>
        </Section>

        <Section title="Profile">
          {p ? (
            <>
              <Row label="Display name">{p.displayName}</Row>
              <Row label="Birth date">{p.birthDate}{p.age != null && <span className="muted"> (age {p.age})</span>}</Row>
              <Row label="Gender">{p.gender}</Row>
              <Row label="Bio">{p.bio ?? DASH}</Row>
              <Row label="Job">{p.job ?? DASH}</Row>
              <Row label="Verified">{yesNo(p.isVerified)}</Row>
              <Row label="Hidden (ghost)">{yesNo(p.isHidden)}</Row>
              <Row label="Boosted until">{p.boostedUntil ? fmtDate(p.boostedUntil) : DASH}</Row>
              <Row label="Interests">{p.interests.length ? p.interests.map((i) => i.slug).join(', ') : DASH}</Row>
              <Row label="Voice intro">{p.hasVoiceIntro ? `Yes${p.voiceDurationSecs != null ? ` · ${p.voiceDurationSecs}s` : ''}` : DASH}</Row>
              <Row label="Place">{[p.city, p.country].filter(Boolean).join(', ') || DASH}</Row>
              <Row label="Height">{p.heightCm != null ? `${p.heightCm} cm` : DASH}</Row>
              <Row label="Weight">{p.weightKg != null ? `${p.weightKg} kg` : DASH}</Row>
              <Row label="Body type">{p.bodyType ?? DASH}</Row>
              <Row label="Smoking">{p.smoking ?? DASH}</Row>
              <Row label="Drinking">{p.drinking ?? DASH}</Row>
              <Row label="Exercise">{p.exercise ?? DASH}</Row>
              <Row label="Diet">{p.diet ?? DASH}</Row>
              <Row label="Pets">{p.pets ?? DASH}</Row>
              <Row label="Languages">{p.languages?.length ? p.languages.join(', ') : DASH}</Row>
              <Row label="Education">{p.education ?? DASH}</Row>
              <Row label="Religion">{p.religion ?? DASH}</Row>
              <Row label="Zodiac">{p.zodiac ?? DASH}</Row>
              <Row label="Relationship goal">{p.relationshipGoal ?? DASH}</Row>
              <Row label="Wants children">{p.wantsChildren ?? DASH}</Row>
            </>
          ) : (
            <Row label="Profile">{DASH}</Row>
          )}
        </Section>

        <Section title="Preferences">
          {pref ? (
            <>
              <Row label="Show gender">{pref.showGender}</Row>
              <Row label="Age range">{`${pref.ageMin}–${pref.ageMax}`}</Row>
              <Row label="Distance">{`${pref.distanceKm} km`}</Row>
            </>
          ) : (
            <Row label="Preferences">{DASH}</Row>
          )}
        </Section>

        <Section title="Location (exact)">
          {loc ? (
            <>
              <Row label="Latitude">{loc.exactLat != null ? <span className="mono">{loc.exactLat.toFixed(6)}</span> : DASH}</Row>
              <Row label="Longitude">{loc.exactLng != null ? <span className="mono">{loc.exactLng.toFixed(6)}</span> : DASH}</Row>
              <Row label="Geohash5"><span className="mono">{loc.geohash5}</span></Row>
              <Row label="Precision">{`${loc.precisionM} m`}</Row>
              <Row label="Source">{loc.source}</Row>
              <Row label="Updated">{fmtDate(loc.updatedAt)}</Row>
              {loc.exactLat != null && loc.exactLng != null && (
                <Row label="Map">
                  <a href={`https://www.openstreetmap.org/?mlat=${loc.exactLat}&mlon=${loc.exactLng}#map=15/${loc.exactLat}/${loc.exactLng}`} target="_blank" rel="noreferrer">
                    Open map
                  </a>
                </Row>
              )}
            </>
          ) : (
            <Row label="Location">{DASH}</Row>
          )}
        </Section>

        <Section title="Activity">
          <Row label="Photos">{c.photos}</Row>
          <Row label="Likes given">{c.swipesYes}</Row>
          <Row label="Passes given">{c.swipesPass}</Row>
          <Row label="Likes received">{c.likesReceived}</Row>
          <Row label="Matches">{c.matches}</Row>
          <Row label="Messages sent">{c.messagesSent}</Row>
          <Row label="Reports against">{c.reportsAgainst}</Row>
          <Row label="Blocks given">{c.blocksGiven}</Row>
          <Row label="Blocks received">{c.blocksReceived}</Row>
        </Section>

        <Section title="Premium & payments">
          <Row label="Premium">{yesNo(user.isPremium)}</Row>
          <Row label="Plan">{effectivePlan}</Row>
          <Row label="Active subs">
            {d.subscriptions.length ? (
              <ul className="detail-list">
                {d.subscriptions.map((s) => (
                  <li key={s.id}>{s.planName ?? s.planId} <span className="muted">until {fmtDate(s.expiresAt)}</span></li>
                ))}
              </ul>
            ) : (
              DASH
            )}
          </Row>
          <Row label="Paid payments">{c.paymentsPaid}</Row>
          <Row label="Total paid">{`${c.totalPaidTon} TON`}</Row>
          <Row label="Recent">
            {d.recentPayments.length ? (
              <ul className="detail-list">
                {d.recentPayments.map((pay) => (
                  <li key={pay.id}>
                    <span className={badgeClass(pay.status)}>{pay.status}</span> {pay.purpose}{' '}
                    {pay.amountTon != null ? `${pay.amountTon} TON` : `${pay.amountMinor} ${pay.currency}`}{' '}
                    <span className="muted">{fmtDate(pay.createdAt)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              DASH
            )}
          </Row>
        </Section>

        <Section title="Onboarding">
          <Row label="Step">{d.onboarding.step}</Row>
          <Row label="Completed">{yesNo(d.onboarding.completed)}</Row>
        </Section>

        <Fragment>
          <PhotosPanel userId={id} />
        </Fragment>
      </div>
    </div>
  );
}
